import React, { useState } from "react";
import {
  Table,
  Typography,
  Tooltip,
  Dropdown,
  Empty,
  notification,
  Modal,
  Input,
} from "antd";
import {
  DeleteOutlined,
  EllipsisOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import moment from "moment";
import { FinetuneDataset } from "../../../model_gen/finetuning";
import type { MenuProps } from "antd";
import { useNavigate } from "react-router-dom";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";

const { confirm } = Modal;

interface DatasetsTableProps {
  datasets: FinetuneDataset[];
  loading: boolean;
  total: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onRefreshData: (page: number) => void;
  setLoading?: (loading: boolean) => void; // Optional loading handler
}

const DatasetsTable: React.FC<DatasetsTableProps> = ({
  datasets,
  loading,
  total,
  currentPage,
  pageSize,
  onPageChange,
  onRefreshData,
  setLoading,
}) => {
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  // Clone modal state
  const [cloneModalVisible, setCloneModalVisible] = useState<boolean>(false);
  const [cloneDataset, setCloneDataset] = useState<FinetuneDataset | null>(
    null
  );
  const [cloneName, setCloneName] = useState<string>("");
  const [cloneDescription, setCloneDescription] = useState<string>("");

  const handleDeleteDataset = (dataset: FinetuneDataset) => {
    const loadingHandler = setLoading || null;

    DatasetService.deleteDataset(dataset.id, loadingHandler, apiKey).subscribe(
      () => {
        notification.success({
          message: "Success",
          description: "Dataset deleted successfully",
        });

        // Determine which page to load after deletion
        let pageToLoad = currentPage;

        // If we deleted the last item on the current page (except the first page)
        if (datasets.length === 1 && currentPage > 1) {
          pageToLoad = currentPage - 1;
          onPageChange(pageToLoad); // Update the page state in parent
        }

        // Refresh data with the calculated page
        onRefreshData(pageToLoad);
      },
      (error: any) => {
        console.error("Error deleting dataset:", error);
        notification.error({
          message: "Error",
          description: `Failed to delete dataset: ${error}`,
        });
      }
    );
  };

  const openCloneModal = (dataset: FinetuneDataset) => {
    setCloneDataset(dataset);
    setCloneName(`${dataset.name} copy`);
    setCloneDescription(dataset.description || "");
    setCloneModalVisible(true);
  };

  const handleClone = () => {
    if (!cloneDataset) return;

    const loadingHandler = setLoading || null;
    const payload = {
      name: cloneName,
      description: cloneDescription,
    };

    DatasetService.cloneDataset(
      cloneDataset.id,
      payload,
      loadingHandler,
      apiKey
    ).subscribe(
      () => {
        notification.success({
          message: "Success",
          description: "Dataset cloned successfully",
        });
        setCloneModalVisible(false);
        // Refresh the current page to show the new dataset
        onRefreshData(currentPage);
      },
      (error: any) => {
        console.error("Error cloning dataset:", error);
        notification.error({
          message: "Error",
          description: `Failed to clone dataset: ${error}`,
        });
      }
    );
  };

  const showDeleteConfirm = (dataset: FinetuneDataset) => {
    confirm({
      title: "Confirm Delete",
      icon: <ExclamationCircleOutlined />,
      content: (
        <>
          <p>Are you sure you want to delete dataset "{dataset.name}"?</p>
          <p>This action cannot be undone.</p>
        </>
      ),
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        handleDeleteDataset(dataset);
      },
    });
  };

  const columns: ColumnsType<FinetuneDataset> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "25%",
      render: (text: string) => (
        <Typography.Text style={{ fontWeight: 500, color: "#1890ff" }}>
          {text}
        </Typography.Text>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (timestamp) => {
        if (!timestamp)
          return <Typography.Text type="secondary">-</Typography.Text>;
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Source",
      key: "source",
      width: "30%",
      render: (_, record) => {
        if (record.intent_group_name) {
          return <Typography.Text>{record.intent_group_name}</Typography.Text>;
        } else if (record.application_name && record.intent_name) {
          return (
            <Typography.Text>
              {record.application_name} / {record.intent_name}
            </Typography.Text>
          );
        } else {
          return <Typography.Text type="secondary">-</Typography.Text>;
        }
      },
    },
    {
      title: "Request Count",
      dataIndex: "request_count",
      key: "request_count",
      width: "15%",
      render: (count: number) => (
        <Typography.Text
          strong
          style={{ color: count > 0 ? "#52c41a" : "#8c8c8c" }}>
          {count ? count.toLocaleString() : "0"}
        </Typography.Text>
      ),
    },
    {
      title: "",
      key: "actions",
      width: "10%",
      render: (_, record) => {
        const items: MenuProps["items"] = [
          {
            key: "clone",
            icon: <CopyOutlined />,
            label: "Clone",
            onClick: (info) => {
              info.domEvent.stopPropagation();
              openCloneModal(record);
            },
          },
          {
            key: "delete",
            icon: <DeleteOutlined />,
            label: "Delete",
            onClick: (info) => {
              info.domEvent.stopPropagation();
              showDeleteConfirm(record);
            },
          },
        ];

        return (
          <div
            style={{ display: "flex", justifyContent: "flex-end" }}
            onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight">
              <a onClick={(e) => e.stopPropagation()}>
                <EllipsisOutlined style={{ fontSize: "16px" }} />
              </a>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  const tableStyles = {
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
  };

  return (
    <>
      <div style={tableStyles.tableContainer}>
        <Table
          columns={columns}
          dataSource={datasets}
          rowKey="id"
          loading={loading}
          rowClassName={() => "interactive-row"}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            onChange: onPageChange,
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} items`,
          }}
          onRow={(record) => ({
            onClick: () => navigate(`/models/datasets/${record.id}`),
          })}
          locale={{
            emptyText: (
              <Empty
                description={
                  <div>
                    <Typography.Text
                      strong
                      style={{ display: "block", marginBottom: "8px" }}>
                      No datasets found
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      Create a new dataset to get started with model
                      fine-tuning.
                    </Typography.Text>
                  </div>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </div>

      {/* Clone Modal */}
      <Modal
        title="Clone Dataset"
        open={cloneModalVisible}
        confirmLoading={loading}
        cancelButtonProps={{ disabled: loading }}
        onOk={handleClone}
        onCancel={() => setCloneModalVisible(false)}
        okText="Clone"
        cancelText="Cancel">
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 4 }}>Name</label>
          <Input
            value={cloneName}
            onChange={(e) => setCloneName(e.target.value)}
            placeholder="Name"
            disabled={loading}
          />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>
            Description
          </label>
          <Input.TextArea
            value={cloneDescription}
            onChange={(e) => setCloneDescription(e.target.value)}
            placeholder="Description"
            rows={4}
            disabled={loading}
          />
        </div>
      </Modal>
    </>
  );
};

export default DatasetsTable;
